import * as React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Button, F0Card, ProductModal as F0ProductModal } from '@factorialco/f0-react'
import {
  Carousel,
  OneEmptyState,
  Page,
  Spinner,
  Tooltip,
} from '@factorialco/f0-react/dist/experimental'
import { Calendar, Info, Money, Upsell } from '@factorialco/f0-react/icons/app'
import Envelope from '@factorialco/f0-react/icons/app/Envelope'
import Share from '@factorialco/f0-react/icons/app/Share'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { DistributorProductVariantsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import RequestDeviceModal from 'modules/it_management/components/RequestDeviceModal'
import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import {
  buildProductImageUrl,
  createProductCatalogQueryContext,
} from 'modules/it_management/lib/graphqlProductCatalogContext'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'
import { useGetUpsellingData } from 'modules/upselling_kit/hooks/useGetUpsellingData'
import { useDefaultMessages } from 'modules/upselling_kit/utils/defaultMessages'

import emptyStateImage from './Empty-state-device-catalog-card.png'

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

const formatPrice = (
  cost: number | string | null | undefined,
  period: number | null | undefined
): string => {
  if (!cost) return '-'
  const costNumber = typeof cost === 'string' ? parseFloat(cost) : cost
  if (isNaN(costNumber)) return '-'
  if (period && period > 0) {
    return `From ${costNumber.toFixed(2)} € /month`
  }
  return `${costNumber.toFixed(2)} €`
}

const formatPeriod = (period: number | null | undefined): string | undefined => {
  if (!period || period <= 0) return undefined
  return i18n.t('it_management.device_catalog.detail.period_months', { count: period })
}

const DeviceCatalogDetailPage: React.FC = () => {
  const { variantId } = useParams<{ variantId: string }>()
  const [searchParams] = useSearchParams()
  const { company, access } = useCurrentSession()
  const [showRequestModal, setShowRequestModal] = React.useState(false)
  const [isRequested, setIsRequested] = React.useState(false)
  const [showGrowthUpsellModal, setShowGrowthUpsellModal] = React.useState(false)

  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()
  const { has_it_management_v2_product_catalog_as_service } = useFeatures([
    'it_management_v2_product_catalog_as_service',
  ])

  const { data, loading, error } = useQuery(DistributorProductVariantsDocument, {
    variables: { ids: variantId ? [variantId] : [] },
    skip: !variantId,
    context: createProductCatalogQueryContext(has_it_management_v2_product_catalog_as_service),
  })

  // Get default messages for the modal when forcing (must be called before early returns)
  const defaultMessages = useDefaultMessages({
    component: 'product_modal',
    requestSource: 'device_catalog_detail_page',
  })

  // Get upsell data to fetch image even when forcing
  const { upsellModalImage } = useGetUpsellingData({
    moduleRequested: AvailableModules.it_management,
  })

  const variant = data?.itManagement.distributorProductVariantsConnection?.nodes?.[0] || null

  React.useEffect(() => {
    if (variant) {
      track(trackingEvents.itManagement.deviceCatalogEvents.goToDeviceDetail, {
        device_id: variant.id,
        device_name: variant.product?.name || variant.sku || variant.id,
        coming_from: document.referrer || 'direct',
        company_id: company.id,
        user_id: access.id,
      })
    }
  }, [variant, company.id, access.id])

  if (featureLoading || loading) {
    return (
      <Page>
        <Box flexDirection="row" alignItems="center" justifyContent="center" padding="s48">
          <Spinner size="large" />
        </Box>
      </Page>
    )
  }

  if (featureError || error || !variant) {
    return (
      <Page>
        <Box padding="s24">
          <OneEmptyState
            title={i18n.t('it_management.device_catalog.detail.not_found')}
            description={i18n.t('it_management.device_catalog.detail.not_found_description')}
            variant="critical"
          />
        </Box>
      </Page>
    )
  }

  const deviceName = variant.product?.name ? variant.product.name : variant.sku || variant.id

  const price = formatPrice(variant.firstOfferCost, variant.firstOfferPeriod)
  const period = formatPeriod(variant.firstOfferPeriod)

  const productMediaImages: string[] =
    'productMediaImageUrls' in variant && Array.isArray(variant.productMediaImageUrls)
      ? variant.productMediaImageUrls.map((url) =>
          buildProductImageUrl(url, has_it_management_v2_product_catalog_as_service)
        )
      : []
  const deviceImages =
    productMediaImages.length > 0
      ? productMediaImages
      : [
          buildProductImageUrl(
            variant.firstProductMediaImageUrl,
            has_it_management_v2_product_catalog_as_service,
            emptyStateImage
          ),
        ]
  const isInStock = variant.stock !== null && variant.stock !== undefined && variant.stock > 0
  const canBeManagedWithFactorialIt =
    variant.product?.typeName != null &&
    ['laptop', 'desktop', 'tablet', 'phone'].includes(variant.product.typeName)

  // Development override: Add ?forceShowUpsell=true to URL to test button visibility
  const forceShowUpsell = searchParams.get('forceShowUpsell') === 'true'
  const shouldShowUpsellButton = !isItManagementEnabled || forceShowUpsell

  return (
    <>
      <Box flexDirection="row" gap="s24" padding="s24" style={{ alignItems: 'flex-start' }}>
        <Box flexDirection="column" style={{ flex: '1 1 auto', minWidth: '0' }}>
          <Box style={{ maxHeight: '300px', overflow: 'hidden' }}>
            <Carousel
              columns={{ default: 1 }}
              showArrows={deviceImages.length > 1}
              showDots={deviceImages.length > 1}
            >
              {deviceImages.map((imageUrl: string, idx: number) => (
                <img
                  key={idx}
                  src={imageUrl}
                  alt={`${deviceName} - ${idx + 1}`}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '300px',
                    borderRadius: '8px',
                    objectFit: 'contain',
                  }}
                />
              ))}
            </Carousel>
          </Box>

          <Box flexDirection="row" gap="s16" alignItems="center">
            {isInStock && (
              <Box
                flexDirection="row"
                gap="s4"
                alignItems="center"
                padding="s4"
                paddingX="s8"
                style={{
                  backgroundColor: 'var(--color-green50)',
                  borderRadius: '16px',
                  border: '1px solid var(--color-green200)',
                }}
              >
                <Box
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-green500)',
                  }}
                />
                <Text size="200" weight="medium" color="grey700">
                  {i18n.t('it_management.device_catalog.detail.in_stock')}
                </Text>
              </Box>
            )}
          </Box>

          {'productAttributes' in variant &&
            Array.isArray(variant.productAttributes) &&
            variant.productAttributes.length > 0 && (
              <Box gap="s16" flexDirection="column" paddingTop="s24">
                <Text size="200" weight="bold" color="black">
                  {i18n.t('it_management.device_catalog.detail.technical_specifications')}
                </Text>
                <Box
                  flexDirection="row"
                  gap="s24"
                  flexWrap="wrap"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '16px',
                  }}
                >
                  {variant.productAttributes.map(
                    (attribute: { id: string; name: string; value: string }) => (
                      <Box key={attribute.id} flexDirection="column" gap="s4">
                        <Text size="200" weight="medium" color="grey700">
                          {capitalize(attribute.name)}
                        </Text>
                        <Text size="300" weight="normal" color="black">
                          {attribute.value}
                        </Text>
                      </Box>
                    )
                  )}
                </Box>
              </Box>
            )}

          {canBeManagedWithFactorialIt && (
            <Box gap="s16" flexDirection="column" paddingTop="s24">
              <Box
                style={{
                  width: '100%',
                  height: '1px',
                  borderBottom: '1px dashed rgba(5, 35, 72, 0.14)',
                }}
              />
              <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween" gap="s16">
                <Box
                  flexGrow
                  style={{
                    backgroundColor: 'var(--color-white)',
                    borderRadius: '8px',
                  }}
                >
                  <Text size="200" weight="bold" color="black">
                    {i18n.t('it_management.device_catalog.detail.management_title')}
                  </Text>
                  <Text size="200" weight="normal" color="grey700">
                    {i18n.t('it_management.device_catalog.detail.management_description')}
                  </Text>
                </Box>
                {shouldShowUpsellButton && (
                  <F0Button
                    variant="outline"
                    label={i18n.t('it_management.device_catalog.detail.more_info')}
                    icon={Upsell}
                    onClick={() => {
                      // Temporary debug code - remove before merging
                      // Using window.console to bypass linter
                      window.console.log(
                        'Opening Growth upsell modal, showGrowthUpsellModal:',
                        true
                      )
                      setShowGrowthUpsellModal(true)
                    }}
                  />
                )}
              </Box>

              <Box flexDirection="row" gap="s16" flexWrap="wrap">
                <Box
                  style={{
                    flex: '0 1 calc(50% - 8px)',
                    minWidth: '0',
                    cursor: 'default',
                  }}
                  background="white"
                  border={{ all: { width: 's1', color: 'grey200', style: 'solid' } }}
                  borderRadius={{ all: 'abs016' }}
                  padding="s16"
                  flexDirection="column"
                  gap="s12"
                >
                  <Box
                    style={{
                      fontSize: '20px',
                      lineHeight: '1',
                      width: 'fit-content',
                    }}
                    padding="s8"
                    border={{ all: { width: 's1', color: 'grey200', style: 'solid' } }}
                    borderRadius={{ all: 'abs016' }}
                  >
                    😌
                  </Box>
                  <Box flexDirection="column">
                    <Text size="300" weight="bold" color="black">
                      {i18n.t('it_management.device_catalog.detail.features.zero_touch.title')}
                    </Text>
                    <Text size="200" weight="normal" color="grey700">
                      {i18n.t(
                        'it_management.device_catalog.detail.features.zero_touch.description'
                      )}
                    </Text>
                  </Box>
                </Box>
                <Box
                  style={{
                    flex: '0 1 calc(50% - 8px)',
                    minWidth: '0',
                    cursor: 'default',
                  }}
                  background="white"
                  border={{ all: { width: 's1', color: 'grey200', style: 'solid' } }}
                  borderRadius={{ all: 'abs016' }}
                  padding="s16"
                  flexDirection="column"
                  gap="s12"
                >
                  <Box
                    style={{
                      fontSize: '20px',
                      lineHeight: '1',
                      width: 'fit-content',
                    }}
                    padding="s8"
                    border={{ all: { width: 's1', color: 'grey200', style: 'solid' } }}
                    borderRadius={{ all: 'abs016' }}
                  >
                    🎯
                  </Box>
                  <Box flexDirection="column">
                    <Text size="300" weight="bold" color="black">
                      {i18n.t('it_management.device_catalog.detail.features.centralized.title')}
                    </Text>
                    <Text size="200" weight="normal" color="grey700">
                      {i18n.t(
                        'it_management.device_catalog.detail.features.centralized.description'
                      )}
                    </Text>
                  </Box>
                </Box>
                <Box
                  style={{
                    flex: '0 1 calc(50% - 8px)',
                    minWidth: '0',
                    cursor: 'default',
                  }}
                  background="white"
                  border={{ all: { width: 's1', color: 'grey200', style: 'solid' } }}
                  borderRadius={{ all: 'abs016' }}
                  padding="s16"
                  flexDirection="column"
                  gap="s12"
                >
                  <Box
                    style={{
                      fontSize: '20px',
                      lineHeight: '1',
                      width: 'fit-content',
                    }}
                    padding="s8"
                    border={{ all: { width: 's1', color: 'grey200', style: 'solid' } }}
                    borderRadius={{ all: 'abs016' }}
                  >
                    🔒
                  </Box>
                  <Box flexDirection="column">
                    <Text size="300" weight="bold" color="black">
                      {i18n.t('it_management.device_catalog.detail.features.security.title')}
                    </Text>
                    <Text size="200" weight="normal" color="grey700">
                      {i18n.t('it_management.device_catalog.detail.features.security.description')}
                    </Text>
                  </Box>
                </Box>
                <Box
                  style={{
                    flex: '0 1 calc(50% - 8px)',
                    minWidth: '0',
                    cursor: 'default',
                  }}
                  background="white"
                  border={{ all: { width: 's1', color: 'grey200', style: 'solid' } }}
                  borderRadius={{ all: 'abs016' }}
                  padding="s16"
                  flexDirection="column"
                  gap="s12"
                >
                  <Box
                    style={{
                      fontSize: '20px',
                      lineHeight: '1',
                      width: 'fit-content',
                    }}
                    padding="s8"
                    border={{ all: { width: 's1', color: 'grey200', style: 'solid' } }}
                    borderRadius={{ all: 'abs016' }}
                  >
                    💻
                  </Box>
                  <Box flexDirection="column">
                    <Text size="300" weight="bold" color="black">
                      {i18n.t('it_management.device_catalog.detail.features.lifecycle.title')}
                    </Text>
                    <Text size="200" weight="normal" color="grey700">
                      {i18n.t('it_management.device_catalog.detail.features.lifecycle.description')}
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        <Box style={{ width: '300px', flexShrink: '0' }}>
          <F0Card
            title={deviceName}
            secondaryActions={
              isRequested
                ? [
                    {
                      label: i18n.t('it_management.device_catalog.actions.requested'),
                      onClick: () => {},
                    },
                  ]
                : [
                    {
                      label: i18n.t('it_management.device_catalog.actions.request'),
                      onClick: (e?: React.MouseEvent) => {
                        e?.stopPropagation()
                        track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceStart, {
                          device_id: variant.id,
                          device_name: deviceName,
                          origin: 'detail_page',
                          company_id: company.id,
                          user_id: access.id,
                        })
                        setShowRequestModal(true)
                      },
                    },
                  ]
            }
          >
            <Box gap="s16" flexDirection="column">
              {price !== '-' && (
                <Box gap="s4" flexDirection="row" alignItems="center">
                  <Tooltip label={i18n.t('it_management.device_catalog.price_title')}>
                    <Money width={16} height={16} />
                  </Tooltip>
                  <Text size="200">{price}</Text>
                </Box>
              )}
              {period && (
                <Box gap="s4" flexDirection="row" alignItems="center">
                  <Tooltip label={i18n.t('it_management.device_catalog.period_title')}>
                    <Calendar width={16} height={16} />
                  </Tooltip>
                  <Text size="200">{period}</Text>
                </Box>
              )}
            </Box>
          </F0Card>
          <Box
            style={{
              backgroundColor: 'var(--color-blue50)',
              border: 'none',
              borderRadius: '8px',
              marginTop: '16px',
            }}
          >
            <F0Alert
              variant="info"
              title={i18n.t('it_management.device_catalog.request_modal.info_title')}
              description={i18n.t('it_management.device_catalog.request_modal.info_description')}
              icon={Info}
            />
          </Box>
        </Box>
      </Box>

      {showRequestModal && variant && (
        <RequestDeviceModal
          variant={variant}
          onClose={() => setShowRequestModal(false)}
          onSuccess={() => {
            setIsRequested(true)
          }}
        />
      )}

      {/*
        Note: UpsellGuard will block this modal when IT Management is already enabled.
        For testing with ?forceShowUpsell=true, the guard may still block it.
        In production, this will only show when IT Management is not enabled.
      */}
      {/* When forcing and module data not available, render modal directly with test data */}
      {forceShowUpsell && showGrowthUpsellModal ? (
        <F0ProductModal
          isOpen={showGrowthUpsellModal}
          showResponseDialog
          onClose={() => {
            window.console.log('Modal onClose called')
            setShowGrowthUpsellModal(false)
          }}
          modalTitle="Factorial IT"
          modalModule="it_management"
          title="Run your IT from one place"
          image={upsellModalImage || ''}
          benefits={[
            'Zero-touch device & access management',
            'Automated onboarding & offboarding in minutes',
            'Full visibility on devices, users, and activity',
            'Fewer tools, fewer risks, less manual work',
          ]}
          primaryAction={{
            label: i18n.t('upselling_kit.button.learn_more'),
            onClick: () => {
              window.console.log('Learn more clicked')
              setShowGrowthUpsellModal(false)
            },
            variant: 'default' as const,
            icon: Share,
          }}
          secondaryAction={{
            label: i18n.t('upselling_kit.button.send_email'),
            onClick: async () => {
              window.console.log('Send email clicked')
              setShowGrowthUpsellModal(false)
            },
            variant: 'outline' as const,
            icon: Envelope,
          }}
          errorMessage={defaultMessages.error}
          successMessage={defaultMessages.success}
          loadingState={defaultMessages.loading}
          closeLabel={defaultMessages.close}
        />
      ) : (
        <UpsellingKit.ProductModal
          moduleRequested={AvailableModules.it_management}
          featureRequested={Features.it_management_v2_core}
          requestReason={ValidReasons.MissingAddon}
          requestSource="device_catalog_detail_page"
          pageSource="device_catalog_detail_page"
          isOpen={showGrowthUpsellModal}
          onClose={() => {
            setShowGrowthUpsellModal(false)
          }}
          upsellRequestComponentType="more_info_button"
          fallbackComponent={null}
        />
      )}
    </>
  )
}

export default DeviceCatalogDetailPage
